import { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Loader2, Radar, AlertTriangle, Sparkles, Plus, RefreshCw, Scale, Package, ClipboardCheck, ArrowRight, Zap, Link2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_META = {
  estado_arte: { label: "Estado del arte", color: "bg-sky-100 text-sky-800 border-sky-300" },
  desarrollo:  { label: "Desarrollo",     color: "bg-violet-100 text-violet-800 border-violet-300" },
  escalado:    { label: "Escalado",       color: "bg-amber-100 text-amber-800 border-amber-300" },
  mercado:     { label: "Mercado",        color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
};

const SEV_META = {
  info:     { label: "Info",     color: "bg-slate-100 text-slate-700" },
  media:    { label: "Media",    color: "bg-amber-100 text-amber-800" },
  alta:     { label: "Alta",     color: "bg-orange-100 text-orange-800" },
  "crítica":{ label: "Crítica",  color: "bg-red-100 text-red-800" },
};

const STATUS_HYP = ["activa", "pausada", "resuelta"];
const CRIT_HYP   = ["baja", "media", "alta", "crítica"];

export default function VigilanciaPage() {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  const authH = { Authorization: `Bearer ${token}` };
  const [tab, setTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [hypotheses, setHypotheses] = useState([]);
  const [signalsForProject, setSignalsForProject] = useState([]);
  const [showNewSignal, setShowNewSignal] = useState(false);
  const [newSignalHyp, setNewSignalHyp] = useState(1);
  const [newSignal, setNewSignal] = useState({ title: "", description: "", severity: "media" });
  const [generatingLLM, setGeneratingLLM] = useState(false);
  const [runningCrossLink, setRunningCrossLink] = useState(false);
  const navigate = useNavigate();

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const [dr, pr] = await Promise.all([
        fetch(`${API}/vt/dashboard`, { headers: authH }).then(r => r.json()),
        fetch(`${API}/pg07/projects`, { headers: authH }).then(r => r.json()),
      ]);
      setDashboard(dr);
      setProjects(pr || []);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const loadProject = async (pid) => {
    if (!pid) return;
    const [h, s] = await Promise.all([
      fetch(`${API}/vt/projects/${pid}/hypotheses`, { headers: authH }).then(r => r.json()),
      fetch(`${API}/vt/signals?project_id=${pid}`, { headers: authH }).then(r => r.json()),
    ]);
    setHypotheses(h || []);
    setSignalsForProject(s || []);
  };

  useEffect(() => { if (selectedProject) loadProject(selectedProject); /* eslint-disable-next-line */ }, [selectedProject]);

  const patchHyp = async (hyp_id, patch) => {
    const r = await fetch(`${API}/vt/hypotheses/${hyp_id}`, {
      method: "PATCH",
      headers: { ...authH, "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    if (r.ok) { toast.success("Hipótesis actualizada"); loadProject(selectedProject); }
    else toast.error("Error");
  };

  const createSignal = async () => {
    if (!newSignal.title.trim()) { toast.error("Título obligatorio"); return; }
    const r = await fetch(`${API}/vt/signals`, {
      method: "POST",
      headers: { ...authH, "Content-Type": "application/json" },
      body: JSON.stringify({ project_id: selectedProject, hypothesis_num: newSignalHyp, ...newSignal }),
    });
    if (r.ok) {
      toast.success("Señal creada");
      setShowNewSignal(false); setNewSignal({ title: "", description: "", severity: "media" });
      loadProject(selectedProject); loadDashboard();
    } else toast.error("Error");
  };

  const closeSignal = async (sid, status = "resuelta") => {
    const r = await fetch(`${API}/vt/signals/${sid}`, {
      method: "PATCH",
      headers: { ...authH, "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    if (r.ok) { toast.success(status === "resuelta" ? "Resuelta" : "Descartada"); loadProject(selectedProject); loadDashboard(); }
  };

  const generateLLM = async () => {
    setGeneratingLLM(true);
    try {
      const r = await fetch(`${API}/vt/generate-signals`, { method: "POST", headers: authH });
      if (r.ok) {
        const j = await r.json();
        toast.success(`LLM generó ${j.generated || 0} señales en ${j.projects_analyzed || 0} proyectos`);
        loadDashboard();
        if (selectedProject) loadProject(selectedProject);
      } else toast.error("Error generando señales");
    } finally { setGeneratingLLM(false); }
  };

  const runCrossLink = async () => {
    if (!window.confirm("¿Ejecutar cross-link Airtable ahora? (Sincroniza proyectos, PMs, partners, docs — puede tardar ~1 min)")) return;
    setRunningCrossLink(true);
    try {
      const r = await fetch(`${API}/cross-link/run-all`, { method: "POST", headers: authH });
      if (r.ok) {
        const j = await r.json();
        const dp = j.dedupe_portfolio_pg07?.matched_pairs || 0;
        const iso = j.import_iso_docs?.inserted || 0;
        const parts = j.sync_partners_from_pg07?.partners_new || 0;
        toast.success(`Cross-link OK · ${dp} vinculados · ${iso} docs ISO · ${parts} partners nuevos`);
        loadDashboard();
      } else toast.error("Error cross-link");
    } finally { setRunningCrossLink(false); }
  };

  return (
    <div className="space-y-6" data-testid="vigilancia-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-cyan-700 font-semibold">Tendencias · 10 preguntas clave</div>
          <h1 className="text-3xl font-black flex items-center gap-2"><Radar className="w-7 h-7 text-cyan-600" /> Tendencias</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">Sistema vivo de señales sobre tecnologías, patentes, competidores, regulación y mercado — activo durante todo el ciclo de vida del proyecto. <a href="https://www.antara.ws/es/blog/vigilancia-tecnologica-proyectos-id-10-preguntas-clave" target="_blank" rel="noopener noreferrer" className="text-cyan-700 hover:underline">10 preguntas Antara ↗</a></p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={loadDashboard} data-testid="vt-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1" />Refrescar</Button>
          <Button variant="outline" onClick={runCrossLink} disabled={runningCrossLink} data-testid="vt-run-crosslink">
            {runningCrossLink ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Link2 className="w-3.5 h-3.5 mr-1" />}
            Ejecutar cross-link
          </Button>
          <Button onClick={generateLLM} disabled={generatingLLM} className="bg-cyan-700 hover:bg-cyan-800" data-testid="vt-generate-llm">
            {generatingLLM ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
            Generar señales IA
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-5 max-w-3xl" data-testid="vt-tabs">
          <TabsTrigger value="dashboard" data-testid="vt-tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="proyecto" data-testid="vt-tab-project">Por proyecto</TabsTrigger>
          <TabsTrigger value="regulacion" data-testid="vt-tab-reg">Regulación</TabsTrigger>
          <TabsTrigger value="recursos" data-testid="vt-tab-res">Recursos críticos</TabsTrigger>
          <TabsTrigger value="estandares" data-testid="vt-tab-std">Estándares</TabsTrigger>
        </TabsList>

        {/* ═══ Dashboard ═══ */}
        <TabsContent value="dashboard" className="mt-4 space-y-4">
          {loading ? <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div> : dashboard && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <StatCard label="Hipótesis totales" value={dashboard.totals.hypotheses_total} icon={ClipboardCheck} tone="cyan" />
                <StatCard label="Hipótesis activas" value={dashboard.totals.hypotheses_active} icon={Radar} tone="emerald" />
                <StatCard label="Hipótesis críticas" value={dashboard.totals.hypotheses_critical} icon={AlertTriangle} tone="red" />
                <StatCard label="Señales abiertas" value={dashboard.totals.signals_open} icon={Zap} tone="amber" />
                <StatCard label="Señales críticas" value={dashboard.totals.signals_critical} icon={AlertTriangle} tone="red" />
              </div>

              <Card>
                <CardContent className="p-4">
                  <h3 className="text-sm font-bold mb-3">Señales abiertas por fase</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(dashboard.by_stage).map(([st, v]) => (
                      <div key={st} className={`border rounded p-3 ${STAGE_META[st].color}`} data-testid={`vt-stage-${st}`}>
                        <div className="text-[10px] uppercase tracking-wider opacity-70">{STAGE_META[st].label}</div>
                        <div className="text-2xl font-black">{v.open}</div>
                        <div className="text-[10px]">{v.critical} críticas</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <div className="grid md:grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <h3 className="text-sm font-bold mb-3">Top proyectos por señales abiertas</h3>
                    {!dashboard.top_projects?.length && <div className="text-xs text-muted-foreground italic">Sin señales aún. Prueba &quot;Generar señales IA&quot;.</div>}
                    <div className="space-y-1.5">
                      {dashboard.top_projects.map(p => (
                        <button key={p.project_id} onClick={() => { setSelectedProject(p.project_id); setTab("proyecto"); }} className="w-full text-left flex items-center gap-2 p-2 rounded hover:bg-slate-50 dark:hover:bg-slate-800" data-testid={`vt-top-project-${p.project_id}`}>
                          <span className="flex-1 truncate text-sm">{p.project_name}</span>
                          {p.critical > 0 && <Badge className="text-[10px] bg-red-100 text-red-800">{p.critical} crítica</Badge>}
                          <Badge variant="outline" className="text-[10px]">{p.open} abiertas</Badge>
                          <ArrowRight className="w-3 h-3 text-muted-foreground" />
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <h3 className="text-sm font-bold mb-3">Señales recientes</h3>
                    {!dashboard.recent_signals?.length && <div className="text-xs text-muted-foreground italic">Sin señales todavía.</div>}
                    <div className="space-y-1.5 max-h-96 overflow-auto">
                      {dashboard.recent_signals.map(s => {
                        const clickable = !!s.source_url;
                        const inner = (
                          <>
                            <div className="text-xs font-medium truncate flex items-center gap-1">
                              {s.title}
                              {clickable && <ArrowRight className="w-3 h-3 text-cyan-600 flex-shrink-0" />}
                            </div>
                            <div className="text-[10px] text-muted-foreground flex gap-1.5 items-center">
                              <Badge className={`${SEV_META[s.severity]?.color} text-[9px] h-4 px-1`}>{s.severity}</Badge>
                              <span>H{s.hypothesis_num}</span>·<span>{s.source}</span>·<span>{new Date(s.detected_at).toLocaleDateString()}</span>
                            </div>
                          </>
                        );
                        return clickable ? (
                          <a key={s.id} href={s.source_url} target="_blank" rel="noopener noreferrer" className="block border-l-2 border-cyan-500 pl-2 py-1 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 rounded transition-colors cursor-pointer" data-testid={`vt-recent-signal-${s.id}`} title={`Abrir fuente: ${s.source_url}`}>
                            {inner}
                          </a>
                        ) : (
                          <div key={s.id} className="border-l-2 border-cyan-500 pl-2 py-1" data-testid={`vt-recent-signal-${s.id}`}>
                            {inner}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* ═══ Por proyecto ═══ */}
        <TabsContent value="proyecto" className="mt-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-xs font-semibold">Proyecto:</label>
            <select
              value={selectedProject}
              onChange={e => setSelectedProject(e.target.value)}
              className="text-sm h-8 rounded border px-2 bg-transparent min-w-[300px]"
              data-testid="vt-project-selector"
            >
              <option value="">— Seleccionar —</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            {selectedProject && <Button size="sm" variant="outline" onClick={() => navigate(`/pg07/extras?project=${selectedProject}`)} data-testid="vt-open-project"><ArrowRight className="w-3 h-3 mr-1" />Fichas del proyecto</Button>}
          </div>

          {!selectedProject && <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Selecciona un proyecto para ver sus 10 hipótesis y señales.</CardContent></Card>}

          {selectedProject && (
            <>
              {["estado_arte", "desarrollo", "escalado", "mercado"].map(st => {
                const stageHyps = hypotheses.filter(h => h.stage === st);
                if (!stageHyps.length) return null;
                return (
                  <Card key={st} className={STAGE_META[st].color.replace(/text-\S+/, "").replace(/bg-\S+/, "bg-white dark:bg-slate-900")}>
                    <CardContent className="p-4">
                      <h3 className={`text-xs uppercase tracking-widest font-black mb-2 inline-block px-2 py-0.5 rounded ${STAGE_META[st].color}`}>{STAGE_META[st].label}</h3>
                      <div className="space-y-2">
                        {stageHyps.map(h => (
                          <div key={h.id} className="border rounded p-3" data-testid={`vt-hyp-${h.hypothesis_num}`}>
                            <div className="flex items-start gap-2 flex-wrap">
                              <div className="w-8 h-8 rounded-full bg-cyan-100 text-cyan-800 flex items-center justify-center font-bold text-sm flex-shrink-0">{h.hypothesis_num}</div>
                              <div className="flex-1 min-w-[250px]">
                                <div className="font-semibold text-sm">{h.title}</div>
                                <div className="text-xs text-muted-foreground">{h.question}</div>
                                {h.notes && <div className="text-xs mt-1 italic">{h.notes}</div>}
                              </div>
                              <div className="flex items-center gap-1 flex-wrap">
                                <select value={h.status} onChange={e => patchHyp(h.id, { status: e.target.value })} className="text-xs h-7 rounded border px-1.5 bg-transparent" data-testid={`vt-hyp-status-${h.hypothesis_num}`}>
                                  {STATUS_HYP.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                                <select value={h.criticality} onChange={e => patchHyp(h.id, { criticality: e.target.value })} className="text-xs h-7 rounded border px-1.5 bg-transparent" data-testid={`vt-hyp-crit-${h.hypothesis_num}`}>
                                  {CRIT_HYP.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                                <Badge variant="outline" className="text-[10px]" data-testid={`vt-hyp-signals-${h.hypothesis_num}`}>{h.signals_open}/{h.signals_total} señales</Badge>
                                <Button size="sm" variant="outline" onClick={() => { setNewSignalHyp(h.hypothesis_num); setShowNewSignal(true); }} data-testid={`vt-hyp-add-signal-${h.hypothesis_num}`}>
                                  <Plus className="w-3 h-3" />Señal
                                </Button>
                              </div>
                            </div>
                            <div className="mt-2 flex items-center gap-2 flex-wrap">
                              <label className="text-[10px] text-muted-foreground">Responsable:</label>
                              <Input value={h.responsible_email || ""} onChange={e => patchHyp(h.id, { responsible_email: e.target.value })} onBlur={e => patchHyp(h.id, { responsible_email: e.target.value })} placeholder="email" className="h-7 text-xs flex-1 max-w-xs" data-testid={`vt-hyp-resp-${h.hypothesis_num}`} />
                            </div>
                            {/* Signals for this hypothesis */}
                            {signalsForProject.filter(s => s.hypothesis_num === h.hypothesis_num).map(s => (
                              <div key={s.id} className={`mt-2 border-l-4 pl-3 py-1.5 ${s.status === "resuelta" ? "border-emerald-400 opacity-60" : s.status === "descartada" ? "border-slate-300 opacity-40" : SEV_META[s.severity]?.color?.includes("red") ? "border-red-500" : "border-amber-500"}`} data-testid={`vt-signal-${s.id}`}>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-xs font-medium">{s.title}</span>
                                  <Badge className={`${SEV_META[s.severity]?.color} text-[9px] h-4 px-1`}>{s.severity}</Badge>
                                  <Badge variant="outline" className="text-[9px] h-4 px-1">{s.source}</Badge>
                                  <Badge variant="outline" className="text-[9px] h-4 px-1">{s.status}</Badge>
                                  <span className="text-[10px] text-muted-foreground">{new Date(s.detected_at).toLocaleDateString()}</span>
                                  {s.status !== "resuelta" && s.status !== "descartada" && (
                                    <>
                                      <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => closeSignal(s.id, "resuelta")}>Resolver</Button>
                                      <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => closeSignal(s.id, "descartada")}>Descartar</Button>
                                    </>
                                  )}
                                </div>
                                {s.description && <div className="text-[11px] text-muted-foreground mt-0.5">{s.description}</div>}
                                {s.source_url && <a href={s.source_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-cyan-700 hover:underline">Fuente ↗</a>}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </>
          )}
        </TabsContent>

        {/* ═══ Regulación / Recursos / Estándares ═══ */}
        <TabsContent value="regulacion" className="mt-4"><RadarView collection="regulations" label="Regulación" icon={Scale} authH={authH} projects={projects} /></TabsContent>
        <TabsContent value="recursos" className="mt-4"><RadarView collection="critical-resources" label="Recursos críticos" icon={Package} authH={authH} projects={projects} /></TabsContent>
        <TabsContent value="estandares" className="mt-4"><RadarView collection="standards" label="Estándares" icon={ClipboardCheck} authH={authH} projects={projects} /></TabsContent>
      </Tabs>

      {/* New signal dialog */}
      <Dialog open={showNewSignal} onOpenChange={setShowNewSignal}>
        <DialogContent data-testid="vt-signal-dialog">
          <DialogHeader><DialogTitle>Nueva señal · Hipótesis {newSignalHyp}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold">Título</label>
              <Input value={newSignal.title} onChange={e => setNewSignal({ ...newSignal, title: e.target.value })} data-testid="vt-signal-title" />
            </div>
            <div>
              <label className="text-xs font-semibold">Descripción</label>
              <Textarea rows={4} value={newSignal.description} onChange={e => setNewSignal({ ...newSignal, description: e.target.value })} data-testid="vt-signal-desc" />
            </div>
            <div>
              <label className="text-xs font-semibold">Severidad</label>
              <select value={newSignal.severity} onChange={e => setNewSignal({ ...newSignal, severity: e.target.value })} className="w-full h-9 rounded border px-2 bg-transparent" data-testid="vt-signal-sev">
                {Object.keys(SEV_META).map(k => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewSignal(false)}>Cancelar</Button>
            <Button onClick={createSignal} data-testid="vt-signal-save">Crear señal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone }) {
  const toneCls = { cyan: "text-cyan-700 bg-cyan-50", emerald: "text-emerald-700 bg-emerald-50", red: "text-red-700 bg-red-50", amber: "text-amber-700 bg-amber-50" }[tone] || "text-slate-700 bg-slate-50";
  return (
    <div className={`rounded-lg border p-3 ${toneCls}`} data-testid={`vt-stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-center gap-2 opacity-80 text-[10px] uppercase tracking-wider">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-3xl font-black mt-1">{value}</div>
    </div>
  );
}

function RadarView({ collection, label, icon: Icon, authH, projects }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", summary: "", risk_level: "media", url: "", affected_projects: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/vt/${collection}`, { headers: authH });
      setItems(await r.json() || []);
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [collection]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nombre obligatorio"); return; }
    const r = await fetch(`${API}/vt/${collection}`, {
      method: "POST",
      headers: { ...authH, "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    if (r.ok) {
      toast.success("Creado");
      setShowNew(false); setForm({ name: "", summary: "", risk_level: "media", url: "", affected_projects: [] });
      load();
    } else toast.error("Error");
  };

  const patch = async (id, changes) => {
    await fetch(`${API}/vt/${collection}/${id}`, {
      method: "PATCH",
      headers: { ...authH, "Content-Type": "application/json" },
      body: JSON.stringify(changes),
    });
    load();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold flex items-center gap-2"><Icon className="w-4 h-4 text-cyan-600" />Radar {label} · {items.length} entradas</h3>
        <Button size="sm" onClick={() => setShowNew(true)} data-testid={`vt-radar-new-${collection}`}><Plus className="w-3 h-3 mr-1" />Nueva entrada</Button>
      </div>
      {loading ? <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-cyan-600" /></div> : (
        <div className="space-y-2">
          {!items.length && <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Sin entradas. Añade la primera.</CardContent></Card>}
          {items.map(it => (
            <Card key={it.id} data-testid={`vt-radar-${collection}-${it.id}`}>
              <CardContent className="p-3 flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <div className="font-semibold text-sm">{it.name}</div>
                  {it.summary && <div className="text-xs text-muted-foreground">{it.summary}</div>}
                  <div className="mt-1 flex gap-1.5 flex-wrap text-[10px]">
                    {it.jurisdiction && <Badge variant="outline">{it.jurisdiction}</Badge>}
                    {it.body && <Badge variant="outline">{it.body}</Badge>}
                    {it.supplier && <Badge variant="outline">Proveedor: {it.supplier}</Badge>}
                    {it.deadline && <Badge variant="outline">Deadline: {it.deadline}</Badge>}
                    {it.url && <a href={it.url} target="_blank" rel="noopener noreferrer" className="text-cyan-700 hover:underline">Enlace ↗</a>}
                    {(it.affected_projects || []).map(pid => {
                      const p = projects.find(pp => pp.id === pid);
                      return <Badge key={pid} variant="outline" className="bg-cyan-50">{p?.name || pid}</Badge>;
                    })}
                  </div>
                </div>
                <select value={it.risk_level} onChange={e => patch(it.id, { risk_level: e.target.value })} className="h-7 text-xs rounded border px-1.5 bg-transparent">
                  {["baja", "media", "alta", "crítica"].map(x => <option key={x}>{x}</option>)}
                </select>
                <select value={it.status} onChange={e => patch(it.id, { status: e.target.value })} className="h-7 text-xs rounded border px-1.5 bg-transparent">
                  {["monitorizado", "acción_requerida", "resuelto"].map(x => <option key={x}>{x}</option>)}
                </select>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent data-testid={`vt-radar-dialog-${collection}`}>
          <DialogHeader><DialogTitle>Nueva entrada · {label}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs font-semibold">Nombre</label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
            <div><label className="text-xs font-semibold">Resumen</label><Textarea rows={3} value={form.summary} onChange={e => setForm({ ...form, summary: e.target.value })} /></div>
            {collection === "regulations" && <div><label className="text-xs font-semibold">Jurisdicción</label><Input value={form.jurisdiction || ""} onChange={e => setForm({ ...form, jurisdiction: e.target.value })} placeholder="UE, España, Global..." /></div>}
            {collection === "standards" && <div><label className="text-xs font-semibold">Organismo</label><Input value={form.body || ""} onChange={e => setForm({ ...form, body: e.target.value })} placeholder="ISO, IEC, CEN, AENOR..." /></div>}
            {collection === "critical-resources" && <div><label className="text-xs font-semibold">Proveedor / Origen</label><Input value={form.supplier || ""} onChange={e => setForm({ ...form, supplier: e.target.value })} /></div>}
            <div><label className="text-xs font-semibold">URL</label><Input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} /></div>
            <div><label className="text-xs font-semibold">Deadline (opcional)</label><Input type="date" value={form.deadline || ""} onChange={e => setForm({ ...form, deadline: e.target.value })} /></div>
            <div>
              <label className="text-xs font-semibold">Nivel de riesgo</label>
              <select value={form.risk_level} onChange={e => setForm({ ...form, risk_level: e.target.value })} className="w-full h-9 rounded border px-2 bg-transparent">
                {["baja","media","alta","crítica"].map(x => <option key={x}>{x}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowNew(false)}>Cancelar</Button><Button onClick={save} data-testid={`vt-radar-save-${collection}`}>Crear</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
